/**
 * Registry of external web apps that are embedded inside the host PWA.
 *
 * WHY A REGISTRY
 * --------------
 * The Library was originally wired up by hand in four places (route, page
 * component, feature helper, bottom nav). Adding a second app that way would
 * have meant copying a ~230-line page file and four more hardcoded branches.
 * Everything about these apps is identical except the label, the path, the URL
 * and the icon — so those four things live here and every consumer derives
 * from this list:
 *
 *   - App.js                    → renders one <Route> per enabled app
 *   - EmbeddedAppPage.js        → the single iframe shell, parameterised by id
 *   - featureHelpers.js         → gating + URL resolution
 *   - BottomNavigation.js       → Services submenu entries + immersive routes
 *   - MobileHamburgerMenu.js    → menu entries
 *
 * ADDING A THIRD APP
 * ------------------
 *   1. Append an entry below.
 *   2. Enable it per-center:
 *        community_features.<id> = {"enabled": true}
 *      (optionally with a "url" key to override defaultUrl for that center).
 *   3. Confirm the target site permits framing — see EmbeddedAppPage.js.
 *
 * Icons are raw SVG path data rather than JSX so this module stays pure data
 * and can be imported from non-React code (helpers, tests) without pulling in
 * React. Every path is drawn on a 24x24 viewBox with currentColor strokes so
 * it matches the rest of the navigation iconography.
 */

export const EMBEDDED_APPS = [
  {
    id: 'library',
    label: 'Library',
    path: '/community/library',
    // Penta-platform shared resource, used by every center unless overridden.
    defaultUrl: 'https://penta-library.netlify.app/',
    // Open book
    iconPaths: [
      'M4 19.5A2.5 2.5 0 0 1 6.5 17H20',
      'M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z',
    ],
  },
  {
    id: 'housing',
    label: 'Housing',
    path: '/community/housing',
    // DEZ housing hub — affordable-housing openings near the center.
    defaultUrl: 'https://dez-housing.netlify.app/',
    // House with door
    iconPaths: [
      'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
      'M9 22V12h6v10',
    ],
  },
];

/** Look up a registry entry by id. Returns null for unknown ids. */
export const getEmbeddedApp = (id) =>
  EMBEDDED_APPS.find((app) => app.id === id) || null;

/**
 * Every path an embedded app can occupy. BottomNavigation uses this to hide
 * the host nav on these routes — they are full-viewport overlays.
 */
export const EMBEDDED_APP_PATHS = EMBEDDED_APPS.map((app) => app.path);

export default EMBEDDED_APPS;
